const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

class ApiClient {
  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (options.token) {
      headers['Authorization'] = `Bearer ${options.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  get(endpoint, token = null) {
    return this.request(endpoint, { method: 'GET', token });
  }

  post(endpoint, body, token = null) {
    return this.request(endpoint, { method: 'POST', body, token });
  }

  put(endpoint, body, token = null) {
    return this.request(endpoint, { method: 'PUT', body, token });
  }

  delete(endpoint, token = null) {
    return this.request(endpoint, { method: 'DELETE', token });
  }

  // Specific API methods
  async getProjects(publishedOnly = false) {
    const params = publishedOnly ? '?published_only=true' : '';
    return this.get(`/projects${params}`);
  }

  async getProject(slug) {
    return this.get(`/projects/${slug}`);
  }

  async createProject(data, token) {
    return this.post('/projects', data, token);
  }

  async updateProject(id, data, token) {
    return this.put(`/projects/${id}`, data, token);
  }

  async deleteProject(id, token) {
    return this.delete(`/projects/${id}`, token);
  }

  async createLead(data) {
    return this.post('/leads', data);
  }

  async getLeads(token, status = null) {
    const params = status ? `?status=${status}` : '';
    return this.get(`/leads${params}`, token);
  }

  async updateLead(id, data, token) {
    return this.put(`/leads/${id}`, data, token);
  }

  async getAvailability(month, year) {
    return this.get(`/bookings/availability?month=${month}&year=${year}`);
  }

  async getTimeSlots(date) {
    return this.get(`/bookings/slots/${date}`);
  }

  async createBooking(data) {
    return this.post('/bookings', data);
  }

  async getBookings(token, date = null) {
    const params = date ? `?date=${date}` : '';
    return this.get(`/bookings${params}`, token);
  }

  async updateBooking(id, data, token) {
    return this.put(`/bookings/${id}`, data, token);
  }

  async deleteBooking(id, token) {
    return this.delete(`/bookings/${id}`, token);
  }

  async getTestimonials(visibleOnly = false) {
    const params = visibleOnly ? '?visible_only=true' : '';
    return this.get(`/testimonials${params}`);
  }

  async createTestimonial(data, token) {
    return this.post('/testimonials', data, token);
  }

  async updateTestimonial(id, data, token) {
    return this.put(`/testimonials/${id}`, data, token);
  }

  async deleteTestimonial(id, token) {
    return this.delete(`/testimonials/${id}`, token);
  }

  async getSettings() {
    return this.get('/settings');
  }

  async updateSettings(data, token) {
    return this.put('/settings', data, token);
  }

  async uploadMedia(file, token) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  // Video Portfolio
  async getVideos() {
    return this.get('/videos');
  }

  async addVideoUrl(url, token) {
    return this.post('/videos', { url }, token);
  }

  async uploadVideoFile(file, token) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE}/videos/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  async deleteVideo(videoId, token) {
    return this.delete(`/videos/${videoId}`, token);
  }

  // Site Content CMS
  async getSiteContent() {
    return this.get('/site-content');
  }

  async updateSiteContent(content, token) {
    return this.put('/site-content', content, token);
  }

  async uploadSiteImage(file, token) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE}/site-content/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  // Landing Page CMS
  async getLandingPage(pageId) {
    return this.get(`/landing-page/${pageId}`);
  }

  async updateLandingPage(pageId, content, token) {
    return this.put(`/landing-page/${pageId}`, content, token);
  }

  // AI Meetings
  async startMeeting(token) {
    return this.post('/meetings/start', {}, token);
  }

  async getMeetings(token) {
    return this.get('/meetings', token);
  }

  async getMeeting(meetingId, token) {
    return this.get(`/meetings/${meetingId}`, token);
  }

  async finishMeeting(meetingId, token) {
    return this.post(`/meetings/${meetingId}/finish`, {}, token);
  }

  // Estimates
  async getEstimates(token) {
    return this.get('/estimates', token);
  }

  async getEstimate(estimateId, token) {
    return this.get(`/estimates/${estimateId}`, token);
  }

  // Pricing Config
  async getPricingConfig(token) {
    return this.get('/pricing-config', token);
  }

  async updatePricingConfig(config, token) {
    return this.put('/pricing-config', config, token);
  }
}

export const api = new ApiClient();
